"use client";

import { useCallback } from "react";
import { useConnection } from "wagmi";

export interface Erc20AssetParams {
  type: "ERC20";
  address: string;
  symbol: string;
  decimals: number;
}

export interface Erc721AssetParams {
  type: "ERC721";
  address: string;
  tokenId: string;
}

export type WatchAssetParams = Erc20AssetParams | Erc721AssetParams;

/**
 * Declining the wallet's "add this asset?" prompt is a normal outcome, not a
 * failure — EIP-1193 reports it as code 4001 (viem/WalletConnect both surface
 * it that way, MetaMask's injected provider sometimes only in the message).
 * Callers use this to stay quiet on a rejection instead of firing an error
 * toast at someone who simply changed their mind.
 */
export function isUserRejection(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const code = (error as { code?: unknown }).code;
  if (code === 4001 || code === "ACTION_REJECTED") return true;
  const message = (error as { message?: unknown }).message;
  return typeof message === "string" && /user (rejected|denied)/i.test(message);
}

/**
 * Gate for the "+ Add to wallet" action. Both remaining connectors
 * (injected, WalletConnect) sit in front of a real wallet with its own UI,
 * unlike Web3Auth's embedded EOA (no wallet UI behind it at all) which used
 * to make this conditional — kept as a hook rather than inlined so callers
 * don't need to change if a future connector needs the same carve-out.
 */
export function useWatchAssetSupported() {
  const { connector } = useConnection();
  return connector != null;
}

/**
 * One wallet_watchAsset helper for both ERC20 (4 ad hoc call sites in the
 * old app's Tokens.tsx/WDoto.tsx/Liquidity.tsx/PoolItem.tsx) and ERC721
 * (V3Liquidity.tsx's new position NFT). Uses the connected connector's own
 * EIP-1193 provider — works the same for the injected connector (forwards
 * straight to window.ethereum) and for WalletConnect (forwards the request
 * over the session to whichever wallet app is on the other end).
 */
export function useWatchAsset() {
  const { connector } = useConnection();
  return useCallback(
    async (params: WatchAssetParams) => {
      if (!connector) throw new Error("No wallet connected");
      const provider = await connector.getProvider();
      const { type, ...options } = params;
      await (provider as { request: (args: { method: string; params?: unknown }) => Promise<unknown> }).request({
        method: "wallet_watchAsset",
        params: { type, options },
      });
    },
    [connector],
  );
}
